"use client";

import Link from "next/link";
import { PlatformError } from "@/lib/api/client";
import { useGamesByIds } from "@/lib/api/hooks";
import { messageFor } from "@/lib/api/messages";
import type { LocalCollectionKind } from "@/lib/player-collections";
import { usePlayer } from "@/components/shell/PlayerContext";
import { useShell } from "@/components/shell/ShellContext";
import { Button } from "@/components/ui/primitives";
import { SectionEmpty, SectionError, TileSkeleton } from "@/components/ui/states";
import { GameGrid } from "./GameSections";

/* Favorites and recently played are the two listings the API cannot serve:
   both live in this browser's storage, so the page can only be rendered on the
   client and only ever shows what this device knows. */

const EMPTY: Record<LocalCollectionKind, { title: string; body: string }> = {
  favorites: {
    title: "No favorites yet",
    body: "Tap the star on any game to keep it here. Favorites are kept on this device, so a different browser starts empty.",
  },
  recent: {
    title: "Nothing played yet",
    body: "Games you open show up here. Recently played is kept on this device, so a different browser starts empty.",
  },
};

const SIGNED_OUT: Record<LocalCollectionKind, { title: string; body: string }> = {
  favorites: {
    title: "Sign in to keep favorites",
    body: "Favorites belong to a player, so this list is only available once you are signed in.",
  },
  recent: {
    title: "Sign in to see what you played",
    body: "Recently played belongs to a player, so this list is only available once you are signed in.",
  },
};

export function LocalCollection({ kind }: { kind: LocalCollectionKind }) {
  const player = usePlayer();
  const { openSignIn } = useShell();
  const ids = kind === "favorites" ? player.favorites : player.recent;
  const collection = useGamesByIds(ids, { enabled: player.ready });

  if (!player.enabled) {
    return (
      <SectionEmpty
        icon={kind === "favorites" ? "star" : "history"}
        title={SIGNED_OUT[kind].title}
        body={SIGNED_OUT[kind].body}
        action={
          <Button type="button" size="md" onClick={() => openSignIn()}>
            Sign in
          </Button>
        }
      />
    );
  }

  /* `ready` is false until the stored list has been read, so an empty state is
     never shown over a list that is about to arrive. */
  if (!player.ready || (ids.length > 0 && collection.isPending)) {
    return (
      <div className="grid gap-[12px] [grid-template-columns:repeat(auto-fill,minmax(120px,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(158px,1fr))]">
        {Array.from({ length: 12 }, (_, index) => (
          <TileSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (ids.length === 0) {
    return (
      <SectionEmpty
        icon={kind === "favorites" ? "star" : "history"}
        title={EMPTY[kind].title}
        body={EMPTY[kind].body}
        action={
          <Link href="/c/all" className="text-[12px] font-bold text-brand-fg-accent hover:underline">
            Browse all games
          </Link>
        }
      />
    );
  }

  if (collection.isError) {
    return (
      <SectionError
        title="This list didn’t load"
        body={messageFor(collection.error as PlatformError, "games")}
      />
    );
  }

  if (collection.games.length === 0) {
    return (
      <SectionEmpty
        icon={kind === "favorites" ? "star" : "history"}
        title="These games are no longer available"
        body="Everything on this list has left the catalogue, or is not available where you are."
      />
    );
  }

  return (
    <div className="flex flex-col gap-[16px]">
      <GameGrid games={collection.games} />
      {kind === "recent" && (
        <button
          type="button"
          onClick={player.clearRecent}
          className="self-start text-[12px] font-bold text-brand-fg-accent hover:underline"
        >
          Clear history
        </button>
      )}
    </div>
  );
}
